/* ═══════════════════════════════════════════════════════════════
   Project Hub — the list surface: header + New project CTA, needs-
   attention strip, compact stat strip, and the full projects table.
   Opening a project (row click, Specs badge, Deliverables hover
   button) is delegated to the caller via onOpenProject — this
   component never owns the dashboard.
   ═══════════════════════════════════════════════════════════════ */
import React, { useMemo, useState } from 'react';
import { Lock } from 'lucide-react';
import { rem } from '../../lib/rem';
import { toAddress } from '../../routing/address';
import { isWeb } from '../../lib/platform';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/Icon';
import { DataTable, Column, GroupOption } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { isArchived, activeOnly } from '../../lib/archive';
import { hasPermission } from '../../../shared/permissions';
import { ProjectService } from '../../services/projectService';
import type {
  Project,
  Organization,
  ProjectType,
  BillingType,
  ProjectPhase,
  Deliverable,
  ProjectScope,
  ProjectScopeAssignment,
  ProjectSpec,
} from '../../../shared/types';
import type { AuthUser } from '../../services/authService';
import { sanitizeProjectName } from './helpers';
import { ProgressBar } from './ProgressBar';
import { DriveImportModal } from './DriveImportModal';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { AttentionStrip } from './AttentionStrip';
import type { AttentionItem } from './projectMutations';
import type { DashboardFocus } from './widgets/context';

type UnmatchedFolder = {
  folderPath: string;
  folderName: string;
  projectNumber: string;
  namePart: string;
  include: boolean;
  editedName: string;
};

export interface HubProps {
  projects: Project[];
  orgs: Organization[];
  projectTypes: ProjectType[];
  billingTypes: BillingType[];
  allPhases: ProjectPhase[];
  allDeliverables: Deliverable[];
  allScopes: ProjectScope[];
  allScopeAssignments: ProjectScopeAssignment[];
  allProjectSpecs: ProjectSpec[];
  attentionItems: AttentionItem[];
  attentionStatus: 'loading' | 'ready' | 'error';
  authUser?: AuthUser | null;
  toast: (msg: string, isError?: boolean) => void;
  onOpenProject: (projectId: string, focus?: DashboardFocus) => void;
  onNewProject: () => void;
  reloadProjects: () => Promise<void>;
}

// Only full NNNNN-NN folder numbers become import candidates — a looser
// guard once mass-created 398 junk projects from bare 5-digit prefixes
// (2026-06-09). Kept verbatim from the old Projects page's autoMatchProjectDrive.
const FOLDER_NUMBER_RE = /^\d{5}-\d{2}$/;

function normalizeProjectNumber(raw: string): string {
  return (raw || '').replace(/\D/g, '');
}

// ─── Thin stat-strip primitives (no card boxes — DESIGN.md §3.1/§3.3) ───
function StatLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontSize: 'var(--font-size-xs)',
        color: 'var(--tm)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '.04em',
      }}
    >
      {children}
    </span>
  );
}
function StatItem({
  label,
  value,
  valueColor,
}: {
  label: string;
  value: number;
  valueColor?: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: rem(6) }}>
      <StatLabel>{label}</StatLabel>
      <span
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 600,
          fontFamily: "'JetBrains Mono', monospace",
          color: valueColor || 'var(--tx)',
        }}
      >
        {value}
      </span>
    </div>
  );
}
function StatSep() {
  return <div style={{ width: rem(1), alignSelf: 'stretch', background: 'var(--bd)' }} />;
}

function statusBadge(s: string) {
  if (s === 'active') return { bg: 'rgba(var(--okr),0.12)', color: 'var(--ok)', text: 'Active' };
  if (s === 'closed') return { bg: 'var(--sa)', color: 'var(--tm)', text: 'Closed' };
  return { bg: 'var(--sa)', color: 'var(--tm)', text: s || 'Unknown' };
}

export function Hub({
  projects,
  orgs,
  projectTypes,
  billingTypes,
  allPhases,
  allDeliverables,
  allScopes,
  allScopeAssignments,
  allProjectSpecs,
  attentionItems,
  attentionStatus,
  authUser,
  toast,
  onOpenProject,
  onNewProject,
  reloadProjects,
}: HubProps) {
  const perms = authUser?.permissions;
  const canCreateProject = hasPermission(perms, 'projects.create');
  const canArchiveProjects = hasPermission(perms, 'projects.archive');
  const canEditProjects = hasPermission(perms, 'projects.edit');

  const [showArchivedProjects, setShowArchivedProjects] = useState(false);

  // ─── Project Drive mapping (desktop only) ───
  const [projectDriveRoot, setProjectDriveRoot] = useState<string>(() => {
    try {
      return localStorage.getItem('specforma.projectDriveRoot') || '';
    } catch {
      return '';
    }
  });
  const [scanningDrive, setScanningDrive] = useState(false);
  const [unmatchedFolders, setUnmatchedFolders] = useState<UnmatchedFolder[]>([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importingFolders, setImportingFolders] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  function saveProjectDriveRoot(p: string) {
    setProjectDriveRoot(p);
    try {
      localStorage.setItem('specforma.projectDriveRoot', p);
    } catch {
      /* ignore */
    }
  }
  async function pickProjectDriveRoot() {
    const dir = await (window as any).api?.selectFolderDialog?.();
    if (dir) saveProjectDriveRoot(dir);
  }

  async function autoMatchProjectDrive() {
    if (!projectDriveRoot) {
      toast('Pick a Project Drive root first', true);
      return;
    }
    if (!(window as any).api?.scanProjectDrive) {
      toast('Scan API not available — restart the app.', true);
      return;
    }
    setScanningDrive(true);
    try {
      const folders = (await (window as any).api.scanProjectDrive(projectDriveRoot)) as Array<{
        folderName: string;
        folderPath: string;
        projectNumber: string;
        namePart: string;
      }>;
      const folderByNum = new Map<string, (typeof folders)[number]>();
      for (const f of folders) folderByNum.set(normalizeProjectNumber(f.projectNumber), f);
      const dbNums = new Set<string>();
      for (const p of projects) {
        const n = normalizeProjectNumber(p.projectNumber || '');
        if (n) dbNums.add(n);
      }
      let matched = 0;
      let updated = 0;
      for (const p of projects) {
        const num = normalizeProjectNumber(p.projectNumber || '');
        if (!num) continue;
        const hit = folderByNum.get(num);
        if (!hit) continue;
        matched++;
        if (p.folderPath !== hit.folderPath) {
          await ProjectService.save({
            ...p,
            folderPath: hit.folderPath,
            updatedAt: new Date().toISOString(),
          });
          updated++;
        }
      }
      const unknown: UnmatchedFolder[] = [];
      for (const f of folders) {
        if (!FOLDER_NUMBER_RE.test(f.projectNumber)) continue;
        const n = normalizeProjectNumber(f.projectNumber);
        if (!n || dbNums.has(n)) continue;
        unknown.push({
          folderPath: f.folderPath,
          folderName: f.folderName,
          projectNumber: f.projectNumber,
          namePart: f.namePart,
          include: true,
          editedName: f.namePart || f.folderName,
        });
      }
      if (matched === 0 && unknown.length === 0) {
        toast(
          `Scanned ${folders.length} folder${folders.length === 1 ? '' : 's'} but no project numbers matched. Check folder naming or pick a different root.`,
          true
        );
      } else {
        const linkedMsg =
          matched > 0
            ? `Auto-match: ${matched}/${projects.length} linked (${updated} updated, ${folders.length} folders scanned)`
            : `Scanned ${folders.length} folder${folders.length === 1 ? '' : 's'}`;
        toast(linkedMsg);
      }
      if (unknown.length > 0 && canCreateProject) {
        setUnmatchedFolders(unknown);
        setShowImportModal(true);
      }
      await reloadProjects();
    } catch (err: any) {
      toast(`Scan failed: ${err.message || err}`, true);
    } finally {
      setScanningDrive(false);
    }
  }

  async function importUnmatchedFolders() {
    const toImport = unmatchedFolders.filter((u) => u.include);
    if (toImport.length === 0) {
      setShowImportModal(false);
      return;
    }
    setImportingFolders(true);
    let created = 0;
    try {
      for (const u of toImport) {
        const newProj: Partial<Project> = {
          id: '',
          name: u.editedName.trim() || u.folderName,
          projectNumber: u.projectNumber,
          folderPath: u.folderPath,
          status: 'active',
          selections: {},
          createdAt: '',
          updatedAt: '',
        };
        try {
          await ProjectService.save(newProj as Project);
          created++;
        } catch (err) {
          console.error('[import folder]', u.projectNumber, err);
        }
      }
      toast(`Imported ${created} project${created === 1 ? '' : 's'} from the drive`);
      await reloadProjects();
      setUnmatchedFolders([]);
      setShowImportModal(false);
    } finally {
      setImportingFolders(false);
    }
  }

  async function bulkDeleteProjects(items: Project[]) {
    for (const p of items) await ProjectService.delete(p.id);
    await reloadProjects();
    toast(`Deleted ${items.length} project${items.length !== 1 ? 's' : ''}`);
  }

  async function doArchiveProjects(items: Project[], archived: boolean) {
    try {
      for (const p of items)
        await ProjectService.setArchived(p.id, archived, authUser?.displayName);
      await reloadProjects();
      toast(
        `${archived ? 'Archived' : 'Restored'} ${items.length} project${items.length !== 1 ? 's' : ''}`
      );
    } catch (err: any) {
      toast(
        `Those projects could not be ${archived ? 'archived' : 'restored'}: ${err.message || 'Unknown error'}`,
        true
      );
    }
  }

  // ─── Derived display helpers (moved verbatim from the old Projects page) ───
  function orgName(id?: string) {
    return orgs.find((o) => o.id === id)?.name || '';
  }
  function typeName(id?: string) {
    return projectTypes.find((t) => t.id === id)?.name || '';
  }
  function billingName(id?: string) {
    return billingTypes.find((t) => t.id === id)?.name || '';
  }
  function getClient(p: Project) {
    return orgName(p.clientOrgId) || p.clientName || '';
  }
  function getProjectScopes(p: Project): ProjectScope[] {
    const scopeIds = new Set(
      allScopeAssignments.filter((a) => a.projectId === p.id).map((a) => a.scopeId)
    );
    return allScopes.filter((s) => scopeIds.has(s.id));
  }
  function getProjectPhase(p: Project): string {
    const projPhases = allPhases
      .filter((ph) => ph.projectId === p.id)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    if (projPhases.length === 0) return '';
    const inProgress = projPhases.filter((ph) => ph.status === 'in-progress');
    if (inProgress.length > 0) return inProgress[inProgress.length - 1].name;
    const complete = projPhases.filter((ph) => ph.status === 'complete');
    if (complete.length === projPhases.length)
      return projPhases[projPhases.length - 1].name + ' (Done)';
    const pending = projPhases.filter((ph) => ph.status === 'pending');
    if (pending.length > 0) return pending[0].name;
    return projPhases[0].name;
  }
  function getProjectDeliverables(p: Project): Deliverable[] {
    return allDeliverables
      .filter((d) => d.projectId === p.id)
      .filter((d) => !d.issued)
      .sort((a, b) => {
        if (a.dueDate && b.dueDate) return String(a.dueDate).localeCompare(String(b.dueDate));
        if (a.dueDate) return -1;
        if (b.dueDate) return 1;
        return 0;
      });
  }

  const specCountByProject = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of allProjectSpecs) {
      if (!s.projectId) continue;
      m.set(s.projectId, (m.get(s.projectId) || 0) + 1);
    }
    return m;
  }, [allProjectSpecs]);

  const activeProjects = projects.filter((p) => p.status === 'active' && !isArchived(p));
  const closedProjects = projects.filter((p) => p.status === 'closed');
  const phaseStats = useMemo(() => {
    let pending = 0;
    let inProgress = 0;
    let complete = 0;
    for (const proj of projects) {
      const pp = allPhases.filter((ph) => ph.projectId === proj.id);
      if (pp.length === 0) continue;
      if (pp.some((ph) => ph.status === 'in-progress')) inProgress++;
      else if (pp.every((ph) => ph.status === 'complete')) complete++;
      else pending++;
    }
    return { pending, inProgress, complete, total: pending + inProgress + complete };
  }, [allPhases, projects]);
  const deliverableStats = useMemo(() => {
    const pending = allDeliverables.filter((d) => d.status === 'pending').length;
    const inProgress = allDeliverables.filter((d) => d.status === 'in-progress').length;
    const complete = allDeliverables.filter((d) => d.status === 'complete').length;
    return { pending, inProgress, complete, total: allDeliverables.length };
  }, [allDeliverables]);

  function renderDelivCell(p: Project) {
    const delivs = getProjectDeliverables(p);
    return (
      <div style={{ position: 'relative' }} className="deliv-cell">
        {delivs.length === 0 ? (
          <span style={{ color: 'var(--tm)' }}>{'–'}</span>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: rem(1) }}>
            {delivs.slice(0, 3).map((d) => {
              const dateText = d.dueDate
                ? new Date(d.dueDate).toLocaleDateString(undefined, {
                    month: 'numeric',
                    day: 'numeric',
                    year: '2-digit',
                  })
                : null;
              return (
                <div
                  key={d.id}
                  style={{ display: 'flex', gap: rem(6), alignItems: 'center', lineHeight: 1.3 }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {d.name}
                  </span>
                  {dateText && (
                    <span
                      style={{
                        color: 'var(--tm)',
                        fontSize: 'var(--font-size-sm)',
                        fontFamily: "'JetBrains Mono', monospace",
                        flexShrink: 0,
                      }}
                    >
                      {dateText}
                    </span>
                  )}
                  {d.issued && <span style={{ color: 'var(--ok)', flexShrink: 0 }}>{'✓'}</span>}
                </div>
              );
            })}
            {delivs.length > 3 && (
              <span style={{ color: 'var(--tm)', fontSize: 'var(--font-size-sm)' }}>
                +{delivs.length - 3} more
              </span>
            )}
          </div>
        )}
        <button
          className="deliv-hover-btn"
          onClick={(e) => {
            e.stopPropagation();
            onOpenProject(p.id, { kind: 'widget', widgetId: 'deliverables' });
          }}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            padding: `${rem(2)} var(--space-2)`,
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--bd)',
            background: 'var(--sf)',
            color: 'var(--ac)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'none',
            whiteSpace: 'nowrap',
            zIndex: 2,
          }}
        >
          Add/Edit
        </button>
        <style>{`.deliv-cell:hover .deliv-hover-btn { display: block !important; }`}</style>
      </div>
    );
  }

  const columns = useMemo<Column<Project>[]>(
    () => [
      {
        key: 'projectNumber',
        label: 'Project #',
        width: 120,
        minWidth: 80,
        getValue: (p) => p.projectNumber || '',
        render: (p) => <span>{p.projectNumber || '–'}</span>,
      },
      {
        key: 'name',
        label: 'Name',
        width: 200,
        minWidth: 120,
        getValue: (p) => p.name || '',
        render: (p) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}>
            {sanitizeProjectName(p.name) || 'Untitled'}
            {p.status === 'closed' && (
              <Icon icon={Lock} em={1} aria-label="Closed" style={{ color: 'var(--tm)' }} />
            )}
          </span>
        ),
      },
      {
        key: 'client',
        label: 'Client',
        width: 140,
        minWidth: 80,
        filterOptions: orgs.map((o) => ({ value: o.id, label: o.name })),
        getValue: (p) => getClient(p),
        render: (p) => <span style={{ color: 'var(--tm)' }}>{getClient(p) || '–'}</span>,
      },
      {
        key: 'type',
        label: 'Type',
        width: 120,
        minWidth: 80,
        filterOptions: projectTypes.map((t) => ({ value: t.id, label: t.name })),
        getValue: (p) => typeName(p.projectTypeId),
        render: (p) => (
          <span style={{ color: 'var(--tm)' }}>{typeName(p.projectTypeId) || '–'}</span>
        ),
      },
      {
        key: 'billingType',
        label: 'Fee Structure',
        width: 130,
        minWidth: 90,
        filterOptions: billingTypes.map((t) => ({ value: t.id, label: t.name })),
        getValue: (p) => billingName(p.billingTypeId),
        render: (p) => (
          <span style={{ color: 'var(--tm)' }}>{billingName(p.billingTypeId) || '–'}</span>
        ),
      },
      {
        key: 'phase',
        label: 'Phase',
        width: 120,
        minWidth: 80,
        getValue: (p) => getProjectPhase(p),
        render: (p) => {
          const phase = getProjectPhase(p);
          return <span style={{ color: phase ? 'var(--tx)' : 'var(--tm)' }}>{phase || '–'}</span>;
        },
      },
      {
        key: 'deliverables',
        label: 'Deliverables',
        width: 260,
        minWidth: 140,
        sortable: true,
        getValue: (p) => getProjectDeliverables(p).length,
        render: (p) => renderDelivCell(p),
      },
      {
        key: 'specs',
        label: 'Specs',
        width: 90,
        minWidth: 64,
        sortable: true,
        getValue: (p) => specCountByProject.get(p.id) || 0,
        render: (p) => {
          const n = specCountByProject.get(p.id) || 0;
          if (n === 0) return <span style={{ color: 'var(--tm)' }}>{'–'}</span>;
          if (!hasPermission(perms, 'project_specs.view')) {
            return (
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 700,
                  color: 'var(--tm)',
                }}
              >
                {n}
              </span>
            );
          }
          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onOpenProject(p.id, { kind: 'takeover', widgetId: 'specs' });
              }}
              title={`${n} spec${n === 1 ? '' : 's'} composed — open specs`}
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 'var(--font-size-sm)',
                fontWeight: 700,
                padding: `${rem(2)} ${rem(9)}`,
                borderRadius: rem(999),
                border: '1px solid rgba(var(--acr),0.30)',
                background: 'rgba(var(--acr),0.10)',
                color: 'var(--ac)',
                cursor: 'pointer',
                lineHeight: 1.4,
              }}
            >
              {n}
            </button>
          );
        },
      },
      {
        key: 'scopes',
        label: 'Scopes',
        width: 140,
        minWidth: 80,
        sortable: true,
        getValue: (p) =>
          getProjectScopes(p)
            .map((s) => s.name)
            .join(', '),
        render: (p) => {
          const ps = getProjectScopes(p);
          if (ps.length === 0) return <span style={{ color: 'var(--tm)' }}>{'–'}</span>;
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: rem(3) }}>
              {ps.map((s) => (
                <span
                  key={s.id}
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    padding: `${rem(1)} ${rem(6)}`,
                    borderRadius: rem(3),
                    background: s.color + '20',
                    color: s.color,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {s.name}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        key: 'status',
        label: 'Status',
        width: 90,
        minWidth: 70,
        filterOptions: [
          { value: 'active', label: 'Active' },
          { value: 'closed', label: 'Closed' },
          { value: 'archived', label: 'Archived' },
        ],
        getValue: (p) => (isArchived(p) ? 'archived' : p.status || 'active'),
        render: (p) => {
          if (isArchived(p)) return <StatusBadge status="archived" />;
          const sb = statusBadge(p.status || 'active');
          return (
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                padding: `${rem(2)} ${rem(6)}`,
                borderRadius: rem(3),
                background: sb.bg,
                color: sb.color,
                fontWeight: 600,
              }}
            >
              {sb.text}
            </span>
          );
        },
      },
    ],
    [
      orgs,
      projectTypes,
      billingTypes,
      allPhases,
      allDeliverables,
      allScopes,
      allScopeAssignments,
      specCountByProject,
    ]
  );

  const groupOptions = useMemo<GroupOption<Project>[]>(
    () =>
      columns.map((c) => ({
        key: c.key,
        label: `By ${c.label}`,
        getValue: (p: Project) => {
          const v = c.getValue ? c.getValue(p) : (p as any)[c.key];
          return v == null || v === '' ? `No ${c.label}` : String(v);
        },
      })),
    [columns]
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: 'var(--space-5)',
      }}
    >
      {/* Header — one primary CTA (+ New project); everything else secondary. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-4)',
          flexShrink: 0,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 700,
              color: 'var(--tx)',
              margin: 0,
            }}
          >
            Projects
          </h1>
          <p
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--tm)',
              margin: 'var(--space-1) 0 0',
            }}
          >
            {activeOnly(projects, showArchivedProjects).length} project
            {activeOnly(projects, showArchivedProjects).length !== 1 ? 's' : ''} &middot; Click a
            row to open it
          </p>
        </div>
        <div
          style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}
        >
          {/* Secondary group — drive tools + archive toggle. "+ New project" stays
              the one visually primary CTA (Josh 2026-09-25 hub hotfix). */}
          <div style={{ display: 'flex', gap: rem(6), alignItems: 'center', flexWrap: 'wrap' }}>
            {!isWeb && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={pickProjectDriveRoot}
                  title={projectDriveRoot || 'No Project Drive root set'}
                >
                  {projectDriveRoot ? 'Change drive root' : 'Set drive root'}
                </Button>
                {canEditProjects && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={autoMatchProjectDrive}
                    disabled={!projectDriveRoot || scanningDrive}
                  >
                    {scanningDrive ? 'Scanning…' : 'Auto-match drive'}
                  </Button>
                )}
              </>
            )}
            <Button
              variant="ghost"
              size="sm"
              aria-pressed={showArchivedProjects}
              onClick={() => setShowArchivedProjects((v) => !v)}
            >
              {showArchivedProjects ? 'Hide archived' : 'Show archived'}
            </Button>
          </div>
          {canCreateProject && <Button onClick={onNewProject}>+ New project</Button>}
        </div>
      </div>

      {!isWeb && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            marginBottom: 'var(--space-3)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--tm)',
            flexShrink: 0,
          }}
        >
          <span>
            Project Drive:{' '}
            {projectDriveRoot ? (
              <span style={{ color: 'var(--tm)' }}>{projectDriveRoot}</span>
            ) : (
              '— not set —'
            )}
          </span>
          {projectDriveRoot && (
            <button
              type="button"
              onClick={() => setConfirmDisconnect(true)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--ac)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                padding: 0,
                minHeight: rem(20),
              }}
            >
              Disconnect
            </button>
          )}
        </div>
      )}

      <AttentionStrip items={attentionItems} status={attentionStatus} onOpen={onOpenProject} />

      {/* Compact stat strip — same numbers as before, one thin row (no cards). */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-4)',
          minHeight: rem(30),
          padding: `0 ${rem(2)}`,
          marginBottom: 'var(--space-4)',
          flexShrink: 0,
          flexWrap: 'wrap',
          rowGap: 'var(--space-2)',
        }}
      >
        <StatItem label="Active" value={activeProjects.length} valueColor="var(--ac)" />
        <StatSep />
        <StatItem label="Closed" value={closedProjects.length} />
        <StatSep />
        <div style={{ display: 'flex', alignItems: 'center', gap: rem(8) }}>
          <StatLabel>Phases</StatLabel>
          <ProgressBar {...phaseStats} />
        </div>
        <StatSep />
        <div style={{ display: 'flex', alignItems: 'center', gap: rem(8) }}>
          <StatLabel>Deliverables</StatLabel>
          <ProgressBar {...deliverableStats} />
        </div>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <DataTable<Project>
          columns={columns}
          data={activeOnly(projects, showArchivedProjects)}
          keyField="id"
          storageKey="projects"
          onRowClick={(p) => onOpenProject(p.id)}
          rowHref={(p) => toAddress({ view: 'projects', projectId: p.id })}
          groupOptions={groupOptions}
          initialSort={{ key: 'projectNumber', direction: 'asc' }}
          emptyIcon="[+]"
          emptyTitle="No Projects Yet"
          emptyMessage="Create a project to get started."
          permissions={perms}
          bulkActions={{
            delete: { permission: 'projects.delete', onDelete: bulkDeleteProjects },
            archive: {
              permission: 'projects.archive',
              onArchive: (items) => doArchiveProjects(items, true),
              onUndo: (items) => doArchiveProjects(items, false),
              label: 'Archive',
            },
            exportCsv: { permission: 'projects.export' },
            extra: (items, meta) => {
              const archivedOnes = items.filter((p) => isArchived(p));
              if (!archivedOnes.length || !canArchiveProjects) return null;
              return (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void doArchiveProjects(archivedOnes, false);
                    meta?.clear();
                  }}
                >
                  Restore {archivedOnes.length}
                </Button>
              );
            },
          }}
        />
      </div>

      {showImportModal && (
        <DriveImportModal
          unmatchedFolders={unmatchedFolders}
          setUnmatchedFolders={setUnmatchedFolders}
          importingFolders={importingFolders}
          setShowImportModal={setShowImportModal}
          importUnmatchedFolders={importUnmatchedFolders}
        />
      )}

      <ConfirmDialog
        open={confirmDisconnect}
        onOpenChange={setConfirmDisconnect}
        title="Disconnect Project Drive?"
        description="This clears the drive root on this computer. Projects already linked to a folder keep their folder path."
        confirmLabel="Disconnect"
        destructive
        onConfirm={() => {
          saveProjectDriveRoot('');
          setConfirmDisconnect(false);
          toast('Project Drive disconnected');
        }}
      />
    </div>
  );
}
